import React, {useEffect, useMemo, useRef, useState} from 'react';


import * as dataUtils from '../../../../utils/dataUtils';
import * as domUtils from '../../../../utils/domUtils';
import * as styleUtils from '../../../../utils/styleUtils';
import * as treeSelectConstants from './treeSelectConstants';

import {I18nMessage} from '../../../contexts/I18n';
import {Icon, iconConstants} from '../../Icon';

import TextInput from '../Input/TextInput';
import TreeSelectItemContent from './TreeSelectItemContent';
import TreeSelectItems from './TreeSelectItems';
import TreeSelectData from './TreeSelectData';
import useOnOutsideClick from '../../../hooks/useOnOutsideClick';

import styles from './TreeSelect.scss';


function TreeSelect(props) {

    const [isExpanded, setIsExpanded] = useState(false);
    const [expandedValues, setExpandedValues] = useState([]);
    const [filterText, setFilterText] = useState(null);

    // using object storage to ensure state diffing
    const [{treeSelectData}, setTreeSelectData] = useState({
        treeSelectData: new TreeSelectData(props.isSingle, props.items, props.values)
    });

    // --- interaction functions
    const onExpandItem = itemValue => setExpandedValues(dataUtils.toggleValue(expandedValues, itemValue));

    const onToggleItem = itemValue => {

        treeSelectData.updateSelectedValues(itemValue);
        if (props.isSingle) {

            // auto-close if single and value has been selected
            setIsExpanded(false);
            // reset filter on toggle
            setFilterText(null);
        }

        setTreeSelectData(({treeSelectData}));

        // and inform embedding component with either single/null or list
        props.onData(treeSelectData.getPropagationValues());
    };

    const onToggleDropdown = (isExpandedOverride = null) => {

        setIsExpanded(typeof isExpandedOverride === 'boolean' ? isExpandedOverride : !isExpanded);
    };

    // using runtime computational values
    const isFiltering = !!filterText;

    // memo-cache for filtering
    const filteredItems = useMemo(() => treeSelectData.getFilteredItems({filterText}), [filterText]);

    // collapse on outside clicks
    const wrapperRef = useRef(null);
    useOnOutsideClick(wrapperRef, () => setIsExpanded(false));

    // autofocus on search
    useEffect(() => {
        if (isExpanded) {
            wrapperRef.current.querySelector('.search input[type="text"]').focus();
        }
    }, [isExpanded]);

    return (
        <div
            className={
                styleUtils.toClasses(
                    styles.formTreeSelect,
                    props.isSingle && styles.isSingle,
                    isExpanded && styles.isExpanded
                )
            }
            onClick={domUtils.stopEvent(onToggleDropdown)}
            ref={wrapperRef}
        >

            <Icon
                icon={
                    isExpanded
                        ? iconConstants.ICON_TRIANGLE_TOP
                        : iconConstants.ICON_TRIANGLE_BOTTOM
                }
            />

            {!treeSelectData.hasSelectedValues() && (
                <span className={styles.pleaseSelect}>
                    <I18nMessage id={'form.tree.select.search.placeholder'}/>
                </span>
            )}

            {treeSelectData.hasSelectedValues() && (
                <ul
                    className={styles.selectedItems}
                    onClick={domUtils.stopEvent(onToggleDropdown)}
                >
                    {treeSelectData.getSelectedValues().map(selectedValue => {

                        const selectedItem = treeSelectData.getItemByValue(selectedValue);

                        return (
                            <li key={`selected-item-${selectedItem.value}`}>
                                <span
                                    className={styles.remove}
                                    onClick={domUtils.stopEvent(() => onToggleItem(selectedItem.value))}
                                >
                                    <Icon icon={iconConstants.ICON_BIN_1}/>
                                </span>
                                <div className={styles.itemContent}>
                                    <TreeSelectItemContent content={selectedItem.content}/>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}

            {isExpanded && (
                <>
                    <div className={styles.search}>
                        <TextInput
                            autocomplete={'off'}
                            maxLength={50}
                            onChange={value => setFilterText(value)}
                            onClick={domUtils.stopEvent()}
                            placeholderI18n={'form.tree.select.search.inlineSearchPlaceholder'}
                            value={filterText}
                        />
                    </div>

                    <div className={styles.dropdownContent}>
                        <TreeSelectItems
                            expandedValues={expandedValues}
                            selectedValues={treeSelectData.getSelectedValues()}
                            level={1}
                            isFiltering={isFiltering}
                            items={filteredItems}
                            onExpandItem={onExpandItem}
                            onToggleItem={onToggleItem}
                        />
                    </div>
                </>
            )}

        </div>
    );
}

TreeSelect.propTypes = {
    ...treeSelectConstants.SHAPE_TREE_SELECT
};


TreeSelect.defaultProps = {
    isSingle: false,
    items: [],
    values: []
};


export default TreeSelect;
